import { createSelector } from '@ngrx/store';

import { Location } from '../../../../generated/graphql.generated';
import { JOB_LOCATION_TYPES, JOB_LOCATION_TYPES_V3 } from '../../../global.constants';
import { isLocationInvalid } from '../../../utilities/locations.util';
import { selectJob } from '../../job-tool.reducer';

import { overviewLocationsFeature } from './overview-locations.feature';

export type OrderedOverviewLocation = {
  locationType: string;
  location: Partial<Location>;
};

export type OrderedOverviewLocationWithId = OrderedOverviewLocation & {
  locationId: string;
};

const LOCATION_TYPES_ORDER = JOB_LOCATION_TYPES.map((locationType) => locationType.key);

export const selectOrderedLocations = createSelector(
  overviewLocationsFeature.selectLocationEditors,
  (editors) => {

    if (!editors) return [];

    const knownLocationTypes = LOCATION_TYPES_ORDER
      .filter((locationType) => locationType in editors);

    const legacyLocationTypes = Object.keys(editors)
      .filter((locationType) => locationType !== 'billing')
      .filter((locationType) => !knownLocationTypes.includes(locationType))
      .sort((a, b) => a.localeCompare(b));

    const orderedLocationTypes = [ ...knownLocationTypes, ...legacyLocationTypes ];

    // Add dock at the end to make route dock to dock
    if (orderedLocationTypes.length > 1 && orderedLocationTypes[0] === 'dock') {
      orderedLocationTypes.push('dock');
    }

    return orderedLocationTypes.map((locationType) => ({
      locationType,
      location: editors[locationType]?.selectedLocation,
    }));
  },
);

const selectCalculateDistanceLocations = createSelector(
  selectOrderedLocations,
  (orderedLocations) => {

    const locations: typeof orderedLocations = [];

    for (const location of orderedLocations) {
      if (location.location?.id) {
        locations.push(location);
      } else {
        break;
      }
    }

    return locations;
  }
);

export const selectCalculateDistanceLocationIds = createSelector(
  selectCalculateDistanceLocations,
  (locations) => locations.map((l) => l.location.id)
);

export const selectCanCalculateDistances = createSelector(
  selectOrderedLocations,
  (orderedLocations) => orderedLocations.length > 1 && orderedLocations.every((l) => l.location?.id),
);

export const selectActiveLocationEditor = createSelector(
  overviewLocationsFeature.selectActiveLocationType,
  overviewLocationsFeature.selectLocationEditors,
  (locationType, editors) => editors[locationType],
);

export const selectActiveJobLocation = createSelector(
  overviewLocationsFeature.selectActiveLocationType,
  selectJob,
  (locationType, job) => job?.locations?.find((l) => l.locationType === locationType),
);

export const selectSectionSaveButtonDisabledMessage = (sectionName: 'Customer' | 'Locations') => createSelector(
  overviewLocationsFeature.selectLocationEditors,
  (locationInputs) => {
    if (!locationInputs) return null;

    if (sectionName === 'Customer' && isLocationInvalid(locationInputs['billing'])) {
      return 'Billing address is invalid.';
    }

    if (sectionName === 'Locations' && JOB_LOCATION_TYPES_V3.some((locationType) => isLocationInvalid(locationInputs[locationType]))) {
      return 'Job locations are invalid.';
    }

    return null;
  }
);
